import {
  MATCH_CANDIDATES,
  NOW,
  OFFICES,
  PEOPLE,
  personById,
  projectById,
  thinSpace,
  type OfficeCode,
} from '@/demo/data'

/**
 * Surface 1.5 — Skills matrix. Every figure, label, route and sentence the
 * page renders resolves through this module or `@/demo/data`; the component
 * carries no literal.
 *
 * WHAT THE DECK GIVES (product/px-platform-mockups.md §1.5): the purpose, the
 * column set, the three panels and the two worked examples (Payroll BE sits
 * with Margot alone; migration and QA trainable in Kosovo in 8 weeks). It
 * states NO figures — so the eight tracked group skills below are authored
 * ONCE here, anchored on the seeded people who carry each skill in
 * `@/demo/data`. Office headcounts are GROUP counts across the 62-person
 * pool; the seed names a page of that pool, so every named holder listed on a
 * row is counted inside that row's office figure and never exceeds it.
 *
 * Coverage is DERIVED, never typed (COVERAGE_RULE): a band typed by hand
 * could disagree with its own headcount. The single-points-of-failure panel is
 * likewise derived from the Critical rows, so the table and the panel cannot
 * drift apart — the sentence is composed from the holder and the project that
 * holder is booked to.
 */

/** A run of prose in which some words are real destinations. */
export type Segment =
  | { kind: 'text'; text: string }
  | { kind: 'link'; label: string; href: string }

/* ── Routes (no href is built by hand in the component) ─────────────────── */

export const personHref = (personId: string): string => `/resources/people/${personId}`
export const BADGES_HREF = '/resources/badges'
export const PIPELINE_HREF = '/resources/pipeline'
export const TRAINING_HREF = '/resources/training'
export const TEMPLATES_HREF = '/library/templates'
export const PROJECTS_HREF = '/projects'

/* ── Chrome ─────────────────────────────────────────────────────────────── */

export const SKILLS_CHROME = {
  moduleLabel: 'PX · Resources',
  breadcrumb: 'Growth › Skills matrix',
  contextPill: 'COVERAGE',
} as const

/* ── The tracked group skills ───────────────────────────────────────────── */

export type Coverage = 'Healthy' | 'Thin' | 'Critical'

/** A seeded person who carries the skill today, with how far they carry it. */
export interface SkillHolder {
  id: string
  /** e.g. "assisted" — the badge ladder's level below Certified. */
  qualifier?: string
}

export interface SkillSeed {
  key: string
  name: string
  heads: Record<OfficeCode, number>
  /** People who own the skill unsupervised today. Never above the headcount. */
  certified: number
  /** Live + pipeline demand over the next two quarters, in hours. */
  demandHours: number
  holders: readonly SkillHolder[]
}

/**
 * The eight skills the group tracks at matrix level. Authored once; long-tail
 * skills stay on the person profiles (see the density note).
 */
const SKILL_SEEDS: readonly SkillSeed[] = [
  {
    key: 'odoo-data-migration',
    name: 'Odoo data migration',
    heads: { NL: 1, BE: 0, FR: 1, KS: 1 },
    certified: 2,
    demandHours: 780,
    holders: [{ id: 'julien-roux' }, { id: 'arta-berisha', qualifier: 'assisted' }],
  },
  {
    key: 'payroll-be',
    name: 'Payroll BE',
    heads: { NL: 0, BE: 1, FR: 0, KS: 0 },
    certified: 1,
    demandHours: 320,
    holders: [{ id: 'margot-claes' }],
  },
  {
    key: 'qa-regression',
    name: 'QA & regression',
    heads: { NL: 1, BE: 0, FR: 1, KS: 2 },
    certified: 3,
    demandHours: 640,
    holders: [{ id: 'blerta-hoxha' }, { id: 'julien-roux' }],
  },
  {
    key: 'retail-vertical',
    name: 'Retail vertical',
    heads: { NL: 3, BE: 0, FR: 2, KS: 1 },
    certified: 5,
    demandHours: 1180,
    holders: [{ id: 'anouk-de-vries' }, { id: 'femke-de-boer' }, { id: 'marc-dubois' }],
  },
  {
    key: 'manufacturing-mrp',
    name: 'Manufacturing & MRP',
    heads: { NL: 1, BE: 0, FR: 1, KS: 0 },
    certified: 2,
    demandHours: 300,
    holders: [{ id: 'camille-laurent' }],
  },
  {
    key: 'accounting-invoicing',
    name: 'Accounting & invoicing',
    heads: { NL: 2, BE: 2, FR: 1, KS: 2 },
    certified: 6,
    demandHours: 900,
    holders: [{ id: 'thomas-peeters' }],
  },
  {
    key: 'integrations-api',
    name: 'Integrations & API',
    heads: { NL: 1, BE: 0, FR: 1, KS: 1 },
    certified: 2,
    demandHours: 560,
    holders: [{ id: 'daan-visser' }],
  },
  {
    key: 'cutover-infrastructure',
    name: 'Cutover & infrastructure',
    heads: { NL: 0, BE: 0, FR: 0, KS: 1 },
    certified: 1,
    demandHours: 260,
    holders: [{ id: 'yll-gashi' }],
  },
] as const

/* ── Coverage, derived ──────────────────────────────────────────────────── */

/**
 * The band rule, written down so a row's colour is an argument and not a
 * decoration: one holder is a single point of failure; three or fewer, or a
 * demand load above what the holders can absorb in two quarters, is thin.
 */
export const COVERAGE_RULE = {
  /** At or below this headcount the skill stops when one person stops. */
  criticalHeads: 1,
  /** At or below this headcount there is no depth to absorb a leaver. */
  thinHeads: 3,
  /** Hours one holder can give this skill over two quarters. */
  demandPerHeadCeiling: 240,
} as const

export const coverageOf = (heads: number, demandHours: number): Coverage => {
  if (heads <= COVERAGE_RULE.criticalHeads) return 'Critical'
  if (heads <= COVERAGE_RULE.thinHeads) return 'Thin'
  return demandHours > heads * COVERAGE_RULE.demandPerHeadCeiling ? 'Thin' : 'Healthy'
}

/** Needs-attention first — the matrix exists to be read from the top. */
const COVERAGE_RANK: Record<Coverage, number> = { Critical: 0, Thin: 1, Healthy: 2 }

export interface HolderLink {
  id: string
  name: string
  href: string
  /** Name, plus the qualifier when the person is not yet certified. */
  label: string
}

export interface SkillMatrixRow {
  key: string
  name: string
  heads: Record<OfficeCode, number>
  totalHeads: number
  certified: number
  demandHours: number
  demandLabel: string
  coverage: Coverage
  holders: readonly HolderLink[]
}

const toRow = (seed: SkillSeed): SkillMatrixRow => {
  const totalHeads = OFFICES.reduce((sum, office) => sum + seed.heads[office.code], 0)
  return {
    key: seed.key,
    name: seed.name,
    heads: seed.heads,
    totalHeads,
    certified: seed.certified,
    demandHours: seed.demandHours,
    demandLabel: `${thinSpace(seed.demandHours)} h`,
    coverage: coverageOf(totalHeads, seed.demandHours),
    holders: seed.holders.map((holder) => {
      const person = personById(holder.id)
      return {
        id: holder.id,
        name: person.name,
        href: personHref(holder.id),
        label: holder.qualifier === undefined ? person.name : `${person.name} · ${holder.qualifier}`,
      }
    }),
  }
}

/** The matrix, ordered Critical → Thin → Healthy, then by demand. */
export const SKILL_ROWS: readonly SkillMatrixRow[] = SKILL_SEEDS.map(toRow).sort(
  (a, b) =>
    COVERAGE_RANK[a.coverage] - COVERAGE_RANK[b.coverage] ||
    b.demandHours - a.demandHours ||
    a.name.localeCompare(b.name)
)

const rowsWith = (coverage: Coverage): readonly SkillMatrixRow[] =>
  SKILL_ROWS.filter((row) => row.coverage === coverage)

export const CRITICAL_ROWS = rowsWith('Critical')
export const THIN_ROWS = rowsWith('Thin')

export const MATRIX_TOTALS = {
  skills: SKILL_ROWS.length,
  heads: SKILL_ROWS.reduce((sum, row) => sum + row.totalHeads, 0),
  certified: SKILL_ROWS.reduce((sum, row) => sum + row.certified, 0),
  demandHours: SKILL_ROWS.reduce((sum, row) => sum + row.demandHours, 0),
} as const

/* ── Table copy ─────────────────────────────────────────────────────────── */

export const OFFICE_COLUMNS = OFFICES.map((office) => ({
  code: office.code,
  /** Full office name for assistive tech — the header shows the code. */
  name: office.name,
}))

export const SKILLS_TABLE = {
  caption: `The ${SKILL_SEEDS.length} tracked group skills across ${OFFICES.length} offices, with certified holders, demand over the next two quarters and the coverage band each falls in. Rows needing attention come first.`,
  columns: {
    skill: 'Skill',
    certified: 'Certified',
    demand: 'Demand · next 2 q',
    coverage: 'Coverage',
  },
  officeGroupLabel: 'Headcount by office',
  certifiedLabel: (certified: number, total: number) => `${certified} of ${total}`,
  certifiedLinkTitle: 'Open the badge register',
  holdersPrefix: 'held by',
  /** Coverage band → the meaning the band carries on this surface. */
  coverageMeaning: {
    Critical: 'one leaver stops it',
    Thin: 'no depth to absorb a leaver',
    Healthy: 'covered',
  } satisfies Record<Coverage, string>,
} as const

/* ── KPI row ────────────────────────────────────────────────────────────── */

const nameList = (rows: readonly SkillMatrixRow[]): string =>
  rows.map((row) => row.name).join(' · ')

export const SKILLS_KPIS = {
  critical: {
    label: 'Critical',
    figure: String(CRITICAL_ROWS.length),
    qualifier: nameList(CRITICAL_ROWS),
  },
  thin: {
    label: 'Thin',
    figure: String(THIN_ROWS.length),
    qualifier: nameList(THIN_ROWS),
  },
  certified: {
    label: 'Certified',
    figure: `${MATRIX_TOTALS.certified} of ${MATRIX_TOTALS.heads}`,
    qualifier: `holders across ${MATRIX_TOTALS.skills} tracked skills`,
  },
  demand: {
    label: 'Demand · next 2 q',
    figure: `${thinSpace(MATRIX_TOTALS.demandHours)} h`,
    qualifier: 'live and pipeline work needing these skills',
  },
} as const

/* ── Panel: single points of failure ────────────────────────────────────── */

export interface SinglePoint {
  key: string
  prose: readonly Segment[]
  meta: string
}

/**
 * One row per Critical skill, composed from that skill's only holder and the
 * project they are booked to — the panel cannot claim a risk the table does
 * not show, and cannot miss one it does.
 */
export const SINGLE_POINTS: readonly SinglePoint[] = CRITICAL_ROWS.map((row) => {
  const [holder] = row.holders
  if (holder === undefined) {
    throw new Error(`Critical skill without a holder: ${row.key}`)
  }
  const person = personById(holder.id)
  const firstName = person.name.split(' ')[0] ?? person.name
  const project = person.currentProject === null ? null : projectById(person.currentProject)
  const stalls: readonly Segment[] =
    project === null
      ? [{ kind: 'text', text: ' — one leaver stalls the work booked against it' }]
      : [
          { kind: 'text', text: ' — one leaver stalls ' },
          { kind: 'link', label: project.client, href: PROJECTS_HREF },
        ]
  return {
    key: row.key,
    prose: [
      { kind: 'text', text: `${row.name} sits with ` },
      { kind: 'link', label: firstName, href: holder.href },
      { kind: 'text', text: ' alone' },
      ...stalls,
    ],
    meta: `${row.demandLabel} of demand next 2 quarters · no second holder in ${OFFICES.length} offices`,
  }
})

export const SINGLE_POINTS_PANEL = {
  label: 'SINGLE POINTS OF FAILURE',
  lede: `${SINGLE_POINTS.length} of the ${MATRIX_TOTALS.skills} tracked skills rest on one person.`,
} as const

/* ── Mint panel: queued to the Competence Center ────────────────────────── */

/** ISO week number of a fixed date — the demo clock is NOW, never Date.now(). */
export const isoWeek = (date: Date): number => {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
  const weekday = d.getUTCDay() === 0 ? 7 : d.getUTCDay()
  d.setUTCDate(d.getUTCDate() + 4 - weekday)
  const yearStart = Date.UTC(d.getUTCFullYear(), 0, 1)
  return Math.ceil(((d.getTime() - yearStart) / 86_400_000 + 1) / 7)
}

export const CURRENT_WEEK = isoWeek(NOW)

export interface CohortRow {
  key: string
  label: string
  people: number
  startWeekLabel: string
  href: string
}

/** Cohorts the matrix has already put in the queue, with weeks out from NOW. */
const COHORT_SEEDS: readonly { skillKey: string; people: number; weeksOut: number }[] = [
  { skillKey: 'odoo-data-migration', people: 4, weeksOut: 3 },
  { skillKey: 'qa-regression', people: 3, weeksOut: 6 },
]

export const QUEUED_COHORTS: readonly CohortRow[] = COHORT_SEEDS.map((seed) => {
  const row = SKILL_ROWS.find((candidate) => candidate.key === seed.skillKey)
  if (row === undefined) throw new Error(`Unknown skill in the cohort queue: ${seed.skillKey}`)
  return {
    key: seed.skillKey,
    label: `${row.name} cohort`,
    people: seed.people,
    startWeekLabel: `starts W${CURRENT_WEEK + seed.weeksOut}`,
    href: PIPELINE_HREF,
  }
})

export const QUEUED_PANEL = {
  label: 'QUEUED TO THE COMPETENCE CENTER',
  peopleLabel: (people: number) => `${people} people`,
  trigger: [
    { kind: 'text', text: 'Nothing is queued by hand: the rows above write this queue — the band a skill sits in and the demand behind it are what open a cohort in the ' },
    { kind: 'link', label: 'talent pipeline', href: PIPELINE_HREF },
    {
      kind: 'text',
      text: `. The skills gap is the hiring plan — ${QUEUED_COHORTS.reduce((sum, cohort) => sum + cohort.people, 0)} seats are booked off this matrix.`,
    },
  ] satisfies readonly Segment[],
} as const

/* ── Panel: cheapest fix ────────────────────────────────────────────────── */

/** Weeks the PX curriculum needs to take a cohort to assisted delivery. */
export const TRAINING_WEEKS = 8

/** Trainer time and material for one Competence Center cohort, in €. */
export const COHORT_COST_EUR = 12_000

const KOSOVO = OFFICES.find((office) => office.code === 'KS')
if (KOSOVO === undefined) {
  throw new Error('No Kosovo office in the seed — the cheapest fix cannot be stated')
}
/** "Kosovo · PX-People" is the office record; the sentence says Kosovo. */
export const KOSOVO_LABEL = KOSOVO.name.split(' · ')[0] ?? KOSOVO.name

const FALLBACK_HIRE = MATCH_CANDIDATES.find((candidate) => candidate.tag === 'Fallback')
if (FALLBACK_HIRE === undefined) {
  throw new Error('No fallback hire in the seeded match — the comparison cannot be stated')
}

/** The two trainable skills the fix names — both sit outside Healthy today. */
const TRAINABLE_KEYS = ['odoo-data-migration', 'qa-regression'] as const

const trainableNames = TRAINABLE_KEYS.map((key) => {
  const row = SKILL_ROWS.find((candidate) => candidate.key === key)
  if (row === undefined) throw new Error(`Unknown skill in the cheapest fix: ${key}`)
  return row.name
})

const MIGRATION_COHORT = QUEUED_COHORTS.find((cohort) => cohort.key === TRAINABLE_KEYS[0])
if (MIGRATION_COHORT === undefined) {
  throw new Error('No migration cohort queued \u2014 the cheapest fix cannot be priced')
}

export const CHEAPEST_FIX = {
  label: 'CHEAPEST FIX',
  prose: [
    {
      kind: 'text',
      text: `${trainableNames.join(' and ')} are trainable in ${KOSOVO_LABEL} in ${TRAINING_WEEKS} weeks with the `,
    },
    { kind: 'link', label: 'PX templates', href: TEMPLATES_HREF },
    { kind: 'text', text: ', taught in the ' },
    { kind: 'link', label: 'training center', href: TRAINING_HREF },
    { kind: 'text', text: ' with anonymised material from delivered projects.' },
  ] satisfies readonly Segment[],
  comparison: `One cohort costs about €${COHORT_COST_EUR / 1000}k in trainer time and lifts ${MIGRATION_COHORT.people} people at once; the fallback is ${FALLBACK_HIRE.label.toLowerCase()} at ${FALLBACK_HIRE.rateLabel} on the rate card — and it ${FALLBACK_HIRE.impact.toLowerCase()}.`,
} as const

/* ── Page copy ──────────────────────────────────────────────────────────── */

export const SKILLS_COPY = {
  title: 'Skills matrix',
  lede: `Where the group is thin — ${MATRIX_TOTALS.skills} tracked skills across ${OFFICES.length} offices, read against live and pipeline demand.`,
  densityNote: `This matrix shows the ${MATRIX_TOTALS.skills} skills the group tracks at coverage level; the long tail of what the ${PEOPLE.length} seeded people can do lives on their profiles, reached from any name above. Demand beyond two quarters is withheld — it is pipeline, not commitment, and coverage decided on it would be a guess.`,
} as const
